import type { ButtonHTMLAttributes, ReactNode } from 'react'

import styles from './Button.module.css'

/**
 * Варианты кнопки.
 *
 * `sacred` — золотая рамка. Единственное место, где золото участвует в
 * интерактиве, и оно здесь не заливка, а обводка: используется для действий
 * над самим раскладом («Проверить расклад»), а не над приложением.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'sacred' | 'danger'
export type ButtonSize = 'lg' | 'md' | 'sm'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  fullWidth?: boolean
  /** Иконка слева. Для `danger` обязательна — см. комментарий ниже. */
  icon?: ReactNode
}

export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  icon,
  disabled,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = [
    styles.button,
    styles[`v-${variant}`],
    styles[`s-${size}`],
    fullWidth ? styles.full : null,
    loading ? styles.loading : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {icon ? <span className={styles.icon}>{icon}</span> : null}
      {/* Лейбл остаётся в потоке даже при загрузке: иначе кнопка меняет ширину
          под пальцем, и попадание в неё становится лотереей. */}
      <span className={styles.label}>{children}</span>
      {loading ? <span className={styles.dots} aria-hidden="true" /> : null}
    </button>
  )
}
